import { zodResolver } from "@hookform/resolvers/zod";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import {
  useIsMutating,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { useRef } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import type { ReviewNoteResponse } from "@bw/shared/types/generated";
import { formatDateTime } from "@bw/shared/utils";

import { addNote } from "../api";

const noteSchema = z.object({
  note: z
    .string()
    .max(2000, "Use 2,000 characters or fewer.")
    .refine((value) => value.trim().length > 0, {
      message: "Enter an internal note.",
    }),
});

type NoteDraft = z.infer<typeof noteSchema>;
type NoteVariables = Readonly<{ orgId: string; note: string }>;
const NOTE_MUTATION_KEY = ["platform", "organization-note"] as const;

type NotesRead = Readonly<{
  data: ReviewNoteResponse[] | undefined;
  isError: boolean;
  isPending: boolean;
  refetch: () => unknown;
}>;

function NoteList({ notes }: { notes: ReviewNoteResponse[] }) {
  return (
    <Stack spacing={2}>
      {notes.map((note) => (
        <Box
          key={note.note_id}
          sx={{ pt: 1.5, borderTop: 1, borderColor: "divider" }}
        >
          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            sx={{ alignItems: "center", flexWrap: "wrap", mb: 0.75 }}
          >
            <Typography variant="caption" sx={{ fontWeight: 700 }}>
              {note.admin_display_name ?? "Author unavailable"}
            </Typography>
            <Chip size="small" variant="outlined" label={note.note_type} />
            <Typography color="text.secondary" variant="caption">
              {formatDateTime(note.created_at)}
            </Typography>
          </Stack>
          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
            {note.note}
          </Typography>
        </Box>
      ))}
    </Stack>
  );
}

export function OrgNotesPanel({
  orgId,
  read,
}: {
  orgId: string;
  read: NotesRead;
}) {
  const queryClient = useQueryClient();
  const submissionLock = useRef(false);
  const mutationKey = [...NOTE_MUTATION_KEY, orgId] as const;
  const sameOrgMutationCount = useIsMutating({
    mutationKey,
  });
  const { register, watch, handleSubmit, reset } = useForm<NoteDraft>({
    resolver: zodResolver(noteSchema, undefined, { mode: "sync" }),
    mode: "onChange",
    defaultValues: { note: "" },
  });
  const note = watch("note");
  const validation = noteSchema.safeParse({ note });
  const noteError = validation.success
    ? undefined
    : validation.error.issues[0]?.message;

  const noteMutation = useMutation({
    mutationKey,
    mutationFn: (variables: NoteVariables) =>
      addNote(variables.orgId, variables.note, "internal"),
    retry: false,
    onSuccess: async (_result, variables) => {
      await queryClient.invalidateQueries({
        queryKey: ["platform", "org-notes", variables.orgId],
      });
    },
  });
  const notePending = noteMutation.isPending || sameOrgMutationCount > 0;

  function submit(draft: NoteDraft) {
    if (submissionLock.current || queryClient.isMutating({ mutationKey }) > 0) {
      return;
    }
    submissionLock.current = true;
    noteMutation.mutate(
      { orgId, note: draft.note.trim() },
      {
        onSuccess: () => reset({ note: "" }),
        onSettled: () => {
          submissionLock.current = false;
        },
      },
    );
  }

  const notes = read.data ?? [];

  return (
    <Paper
      component="section"
      aria-labelledby="review-notes-heading"
      variant="outlined"
      sx={{ p: { xs: 2, md: 2.5 } }}
    >
      <Stack spacing={2}>
        <Typography
          id="review-notes-heading"
          component="h2"
          variant="h6"
          sx={{ fontWeight: 700 }}
        >
          Review Notes
        </Typography>

        {read.isPending ? (
          <Box
            role="status"
            aria-label="Loading review notes"
            aria-live="polite"
            sx={{ display: "flex", justifyContent: "center", py: 4 }}
          >
            <CircularProgress size={32} />
          </Box>
        ) : read.isError ? (
          <Alert
            severity="error"
            role="alert"
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => void read.refetch()}
              >
                Retry
              </Button>
            }
          >
            Review notes could not be loaded. Try again.
          </Alert>
        ) : notes.length === 0 ? (
          <Typography color="text.secondary" variant="body2">
            No notes yet
          </Typography>
        ) : (
          <NoteList notes={notes} />
        )}

        <Box
          component="form"
          noValidate
          onSubmit={handleSubmit(submit)}
          sx={{ pt: 1 }}
        >
          <Stack spacing={1.5}>
            <TextField
              {...register("note")}
              fullWidth
              multiline
              minRows={3}
              disabled={notePending}
              label="Internal note"
              placeholder="Add a review note..."
              error={note.length > 0 && !validation.success}
              helperText={note.length > 0 ? noteError : undefined}
              slotProps={{ htmlInput: { maxLength: 2000 } }}
            />
            {noteMutation.isError && (
              <Alert severity="error" role="alert">
                The note could not be added. Review it and try again.
              </Alert>
            )}
            <Box>
              <Button
                type="submit"
                size="small"
                variant="contained"
                disabled={notePending || !validation.success}
                sx={{ "&&": { pointerEvents: "auto" } }}
              >
                {noteMutation.isPending ? "Adding…" : "Add Note"}
              </Button>
            </Box>
          </Stack>
        </Box>
      </Stack>
    </Paper>
  );
}
